import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { 
  getScreenTimeData, 
  getAppUsageData, 
  getUserGoals,
  getFocusSessionHistory,
  getUserInsights
} from '../firebase/dbService';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

const DataContext = createContext();

export function useData() {
  return useContext(DataContext);
}

export function DataProvider({ children }) {
  const { currentUser } = useAuth();
  
  const [screenTimeData, setScreenTimeData] = useState([]);
  const [appUsageData, setAppUsageData] = useState([]);
  const [goalsData, setGoalsData] = useState([]);
  const [focusSessions, setFocusSessions] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch screen time data for a date range
  const fetchScreenTime = useCallback(async (dateFrom, dateTo) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const result = await getScreenTimeData(currentUser.uid, dateFrom, dateTo);
      
      if (result.success) {
        setScreenTimeData(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch app usage data for a date range
  const fetchAppUsage = useCallback(async (dateFrom, dateTo) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const result = await getAppUsageData(currentUser.uid, dateFrom, dateTo);
      
      if (result.success) {
        setAppUsageData(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch user goals - wrap in useCallback
  const fetchGoals = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const result = await getUserGoals(currentUser.uid);
      
      if (result.success) {
        setGoalsData(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch focus sessions - wrap in useCallback
  const fetchFocusSessions = useCallback(async (limit = 10) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const result = await getFocusSessionHistory(currentUser.uid, limit);
      
      if (result.success) {
        setFocusSessions(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch insights - wrap in useCallback
  const fetchInsights = useCallback(async (limit = 20) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const result = await getUserInsights(currentUser.uid, limit);
      
      if (result.success) {
        setInsights(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch today's data
  const fetchTodayData = useCallback(async () => {
    const today = new Date();
    const startOfToday = startOfDay(today);
    const endOfToday = endOfDay(today);
    
    await Promise.all([
      fetchScreenTime(startOfToday, endOfToday),
      fetchAppUsage(startOfToday, endOfToday)
    ]);
  }, [fetchScreenTime, fetchAppUsage]);

  // Fetch weekly data - wrap in useCallback
  const fetchWeekData = useCallback(async () => {
    const today = new Date();
    const weekStart = startOfWeek(today);
    const weekEnd = endOfWeek(today);
    
    await Promise.all([
      fetchScreenTime(weekStart, weekEnd),
      fetchAppUsage(weekStart, weekEnd)
    ]);
  }, [fetchScreenTime, fetchAppUsage]);

  // Fetch monthly data
  const fetchMonthData = useCallback(async () => {
    const today = new Date();
    const monthStart = startOfMonth(today);
    const monthEnd = endOfMonth(today);
    
    await Promise.all([
      fetchScreenTime(monthStart, monthEnd),
      fetchAppUsage(monthStart, monthEnd)
    ]);
  }, [fetchScreenTime, fetchAppUsage]);

  // Format screen time data for chart
  const getScreenTimeChartData = (timeRange = 'week') => {
    if (screenTimeData.length === 0) return [];
    
    if (timeRange === 'today') {
      // Format hourly data
      return screenTimeData[0]?.hourlyBreakdown.map(entry => ({
        hour: format(new Date(entry.timestamp), 'ha'),
        minutes: entry.minutes
      })) || [];
    } else if (timeRange === 'week') {
      // Format daily data
      return screenTimeData.map(entry => ({
        day: format(new Date(entry.date), 'E'), // Day abbreviation (Mon, Tue, etc.)
        hours: entry.totalTime / 60 // Convert minutes to hours
      }));
    } else {
      // Monthly data - group by week
      const weeklyData = [];
      // Grouping logic would go here
      return weeklyData;
    }
  };

  // Get top app usage
  const getTopAppUsage = (count = 5) => {
    return [...appUsageData]
      .sort((a, b) => b.timeSpent - a.timeSpent)
      .slice(0, count);
  };

  // Load initial data when user changes
  useEffect(() => {
    if (currentUser) {
      Promise.all([
        fetchGoals(),
        fetchWeekData(),
        fetchFocusSessions(),
        fetchInsights()
      ]).then(() => {
        setLoading(false);
      });
    } else {
      setScreenTimeData([]);
      setAppUsageData([]);
      setGoalsData([]);
      setFocusSessions([]);
      setInsights([]);
      setLoading(false);
    }
  }, [currentUser, fetchGoals, fetchWeekData, fetchFocusSessions, fetchInsights]);

  const value = {
    screenTimeData,
    appUsageData,
    goalsData,
    focusSessions,
    insights,
    loading,
    error,
    fetchScreenTime,
    fetchAppUsage,
    fetchGoals,
    fetchFocusSessions,
    fetchInsights,
    fetchTodayData,
    fetchWeekData,
    fetchMonthData,
    getScreenTimeChartData,
    getTopAppUsage
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
} 